import type { SuccessBoxProps } from '@/components/landing-page/components/success-box';
import { Check, X, CircleAlert, CircleQuestionMark } from 'lucide-react';
import clsx from 'clsx';
import IconCheckCircle from '@/icons/check-circle';
import IconQuestion from '@/icons/question';
import IconExclamation from '@/icons/exclamation';
import IconExclamationTriangle from '@/icons/exclamation-triangle';
import { defaults } from '@/components/landing-page/components/success-box';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { ID } from '@/utils/enums';
import { cn } from '@/utils/helper';
import v1 from '@/components/landing-page/components/success-box/success-box-v1.module.css';
import v2 from '@/components/landing-page/components/success-box/success-box-v2.module.css';

const SuccessBox = (props: SuccessBoxProps) => {
  const { head = defaults.head, body = defaults.body, color = defaults.color } = props;

  console.log('COLOR', color);

  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);

  return (
    <div className={cn(v1.SuccessBox, v2.SuccessBox)}>
      <style type='text/css'>
        {`
          .${v1.SuccessBox} {
            --success-box: ${color};
          }
        `}
      </style>
      <div id={ID.LANDING_PAGE_SUCCESS_BOX} className={cn(v1.Head, v2.Head)}>
        <IconCheckCircle className={cn(v1.HeadIcon, v2.HeadIcon)} />
        <h5 className={cn(v1.HeadText, v2.HeadText)}>
          {head.primary && <span className={cn(v1.HeadTextPrimary, v2.HeadTextPrimary)}>{head.primary}</span>}
          {head.secondary && <span className={cn(v1.HeadTextSecondary, v2.HeadTextSecondary)}>{head.secondary}</span>}
        </h5>
      </div>
      <div className={cn(v1.Main, v2.Main)}>
        {body
          .filter(({ data }) => !!data)
          .map((item, i) => {
            switch (item.type) {
              case 'title':
                return (
                  <h6 key={i} className={cn(v1.Title, v2.Title)} dangerouslySetInnerHTML={{ __html: item.data }} />
                );
              case 'html':
                return <div key={i} className={cn(v1.Html, v2.Html)} dangerouslySetInnerHTML={{ __html: item.data }} />;
              case 'list':
                return (
                  <ul key={i} className={cn(v1.List, v2.List)}>
                    {item.data.map((item, i) => (
                      <li
                        key={i}
                        className={clsx(cn(v1.Item, v2.Item), {
                          [cn(v1.ItemCheck, v2.ItemCheck)]: item.type === 'check',
                          [cn(v1.ItemQuestion, v2.ItemQuestion)]: item.type === 'question',
                          [cn(v1.ItemExclamation, v2.ItemExclamation)]: item.type === 'exclamation',
                          [cn(v1.ItemCross, v2.ItemCross)]: item.type === 'cross',
                        })}
                      >
                        <div className={cn(v1.Section, v2.Section)}>
                          {item.type === 'check' && <Check strokeWidth={4} className={cn(v1.Icon, v2.Icon)} />}
                          {item.type === 'question' ? (
                            cssVersion === 1 ? (
                              <IconQuestion className={cn(v1.Icon, v2.Icon)} />
                            ) : (
                              <CircleQuestionMark className={cn(v1.Icon, v2.Icon)} />
                            )
                          ) : null}
                          {item.type === 'exclamation' ? (
                            cssVersion === 1 ? (
                              <IconExclamation className={cn(v1.Icon, v2.Icon)} />
                            ) : (
                              <CircleAlert className={cn(v1.Icon, v2.Icon)} />
                            )
                          ) : null}
                          {item.type === 'cross' && <X className={cn(v1.Icon, v2.Icon)} />}
                          <div className={cn(v1.Text, v2.Text)} dangerouslySetInnerHTML={{ __html: item.content }} />
                        </div>
                        {!!item.subContent.length && (
                          <div className={v1.Warning}>
                            <IconExclamationTriangle
                              className={clsx(v1.WarningIcon, {
                                [v1.WarningIconSingle]: item.subContent.length === 1,
                                [v1.WarningIconMultiple]: item.subContent.length === 3,
                              })}
                            />
                            <div className={v1.WarningContent}>
                              {item.subContent.map((line, i) => (
                                <p key={i} className={v1.WarningText} dangerouslySetInnerHTML={{ __html: line }} />
                              ))}
                            </div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                );
            }
          })}
      </div>
    </div>
  );
};

export default SuccessBox;
